import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ChevronRight } from "lucide-react";

const LOGO = "/images/ndr-logo.png";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/"); setMobileOpen(false); };
  const isActive = (path) => location.pathname === path;
  const closeMenu = () => setMobileOpen(false);

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white shadow-sm border-b border-slate-200">
        <div className="section-container">
          <div className="flex items-center justify-between h-14 sm:h-16">

            {/* Logo — responsive sizing via .navbar-logo CSS class */}
            <Link to="/" data-testid="navbar-logo" className="flex-shrink-0" onClick={closeMenu}>
              <div
                className="navbar-logo"
                style={{ backgroundImage: `url(${LOGO})` }}
                role="img"
                aria-label="NextDayRefund.com"
              />
            </Link>

            {/* Desktop Nav Links — 1024px+ only (avoids iPad overflow) */}
            <div className="hidden lg:flex items-center gap-0.5 xl:gap-1">
              <Link to="/find-provider" data-testid="nav-find-provider"
                className={`px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${isActive("/find-provider") ? "text-[#003366] bg-[#003366]/8 font-semibold" : "text-slate-600 hover:text-[#003366] hover:bg-slate-100"}`}>
                Find a Provider
              </Link>
              <Link to="/advertise" data-testid="nav-advertise"
                className={`px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${isActive("/advertise") ? "text-[#003366] bg-[#003366]/8 font-semibold" : "text-slate-600 hover:text-[#003366] hover:bg-slate-100"}`}>
                Advertise Here
              </Link>
              <Link to="/map" data-testid="nav-map"
                className={`px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${isActive("/map") ? "text-[#003366] bg-[#003366]/8 font-semibold" : "text-slate-600 hover:text-[#003366] hover:bg-slate-100"}`}>
                Territory Map
              </Link>
              <Link to="/jobs" data-testid="nav-jobs"
                className={`px-2 xl:px-3 py-2 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${isActive("/jobs") ? "text-[#2ECC71] font-bold" : "text-[#2ECC71] hover:bg-[#2ECC71]/10"}`}>
                Jobs
              </Link>
            </div>

            {/* Desktop Actions — 1024px+ only */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-3">
              <Link to="/claim" data-testid="nav-claim-cta"
                className="btn-action !py-2 !px-3 xl:!px-5 !text-sm whitespace-nowrap">
                <span className="hidden xl:inline">Claim Your Zip Code</span>
                <span className="xl:hidden">Claim Zip</span>
              </Link>
              {user ? (
                <div className="flex items-center gap-2">
                  <Link to={user.role === "admin" ? "/admin" : "/portal"} data-testid="nav-portal-link"
                    className="border-2 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap">
                    {user.role === "admin" ? "Admin" : "Portal"}
                  </Link>
                  <button onClick={handleLogout} data-testid="nav-logout-btn"
                    className="text-sm text-slate-400 hover:text-slate-700 transition-colors">
                    Log Out
                  </button>
                </div>
              ) : (
                <Link to="/login" data-testid="nav-login-link"
                  className="border-2 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap">
                  Sign In
                </Link>
              )}
            </div>

            {/* Mobile / Tablet toggle — below 1024px */}
            <div className="lg:hidden flex items-center gap-2">
              <Link to="/claim" data-testid="mobile-claim-top"
                className="btn-action !py-1.5 !px-3 !text-xs whitespace-nowrap">
                Claim Zip
              </Link>
              <button
                className="p-2 text-slate-500 hover:text-[#003366] rounded-md hover:bg-slate-100 transition-colors"
                onClick={() => setMobileOpen(o => !o)}
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Fixed overlay menu ── zero layout shift, no horizontal scroll ── */}
      {mobileOpen && (
        <>
          {/* Dimmed backdrop — click to close */}
          <div
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={closeMenu}
            data-testid="mobile-menu-backdrop"
            aria-hidden="true"
          />

          {/* Slide-in panel from the right */}
          <div
            className="fixed top-0 right-0 h-full w-72 max-w-[85vw] bg-white z-50 shadow-2xl overflow-y-auto lg:hidden"
            data-testid="mobile-menu"
          >
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white sticky top-0">
              <div
                className="navbar-logo-sm"
                style={{ backgroundImage: `url(${LOGO})` }}
                aria-hidden="true"
              />
              <button
                onClick={closeMenu}
                className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 transition-colors"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            {/* Nav items */}
            <div className="py-3 px-3 space-y-1">
              <Link to="/find-provider" onClick={closeMenu}
                className="flex items-center justify-between px-4 py-3 text-slate-600 hover:text-[#003366] hover:bg-slate-50 rounded-lg transition-colors font-medium"
                data-testid="mobile-find-provider">
                Find a Provider <ChevronRight size={16} />
              </Link>
              <Link to="/advertise" onClick={closeMenu}
                className="flex items-center justify-between px-4 py-3 text-slate-600 hover:text-[#003366] hover:bg-slate-50 rounded-lg transition-colors font-medium"
                data-testid="mobile-advertise">
                Advertise Here <ChevronRight size={16} />
              </Link>
              <Link to="/map" onClick={closeMenu}
                className="flex items-center justify-between px-4 py-3 text-slate-600 hover:text-[#003366] hover:bg-slate-50 rounded-lg transition-colors font-medium"
                data-testid="mobile-map">
                Territory Map <ChevronRight size={16} />
              </Link>
              <Link to="/jobs" onClick={closeMenu}
                className="flex items-center justify-between px-4 py-3 font-semibold rounded-lg transition-colors"
                style={{ color: "#2ECC71" }}
                data-testid="mobile-jobs">
                Jobs Board <ChevronRight size={16} />
              </Link>

              <div className="border-t border-slate-200 pt-3 mt-2 space-y-2">
                <Link to="/claim" onClick={closeMenu}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-[#2ECC71] hover:bg-[#27AE60] text-white font-bold rounded-xl text-sm transition-colors"
                  data-testid="mobile-claim-menu">
                  Claim Your ZIP Code →
                </Link>
                {user ? (
                  <>
                    <Link to={user.role === "admin" ? "/admin" : "/portal"} onClick={closeMenu}
                      className="flex items-center justify-between px-4 py-3 text-[#003366] font-semibold hover:bg-slate-50 rounded-lg transition-colors"
                      data-testid="mobile-portal-link">
                      {user.role === "admin" ? "Admin Dashboard" : "My Portal"} <ChevronRight size={16} />
                    </Link>
                    <button onClick={handleLogout}
                      className="w-full text-left px-4 py-3 text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                      data-testid="mobile-logout-btn">
                      Sign Out
                    </button>
                  </>
                ) : (
                  <Link to="/login" onClick={closeMenu}
                    className="flex items-center justify-between px-4 py-3 text-[#003366] font-semibold border border-[#003366]/30 rounded-lg hover:bg-slate-50 transition-colors"
                    data-testid="mobile-login-link">
                    Sign In <ChevronRight size={16} />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
